import { useState, useEffect, useCallback } from 'react';
import { Trash2, RefreshCw, X, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { API } from './constants';

export function CrmCorbeille({ onRestored }) {
  const [deleted, setDeleted] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDeleted = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/crm/corbeille`);
      setDeleted(await res.json());
    } catch { toast.error('Erreur chargement corbeille'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDeleted(); }, [fetchDeleted]);

  const restore = async (id) => {
    try {
      await fetch(`${API}/api/crm/prospects/${id}/restore`, { method: 'POST' });
      toast.success('Prospect restaure');
      setDeleted(prev => prev.filter(p => p.id !== id));
      if (onRestored) onRestored();
    } catch { toast.error('Erreur'); }
  };

  const permDelete = async (id, nom) => {
    if (!window.confirm(`Supprimer ${nom} definitivement ? Cette action est irreversible.`)) return;
    try {
      await fetch(`${API}/api/crm/corbeille/${id}/permanent`, { method: 'DELETE' });
      toast.success('Supprime definitivement');
      setDeleted(prev => prev.filter(p => p.id !== id));
    } catch { toast.error('Erreur'); }
  };

  const emptyTrash = async () => {
    if (!window.confirm(`Vider la corbeille ? ${deleted.length} prospect(s) seront supprimes definitivement.`)) return;
    try {
      await fetch(`${API}/api/crm/corbeille/vider`, { method: 'DELETE' });
      toast.success('Corbeille videe');
      setDeleted([]);
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-4" data-testid="crm-corbeille">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trash2 size={16} className="text-red-400" />
          <h3 className="text-sm font-bold text-gray-700">Corbeille ({deleted.length})</h3>
        </div>
        {deleted.length > 0 && (
          <Button onClick={emptyTrash} size="sm" variant="outline" className="text-xs text-red-400 border-red-200 hover:bg-red-50" data-testid="empty-trash-btn">
            Vider la corbeille
          </Button>
        )}
      </div>
      {deleted.length === 0 ? (
        <div className="text-center py-12">
          <Trash2 size={32} className="mx-auto text-gray-200 mb-3" />
          <p className="text-sm text-gray-400">La corbeille est vide</p>
          <p className="text-xs text-gray-300 mt-1">Les prospects supprimes apparaitront ici et pourront etre restaures</p>
        </div>
      ) : (
        <div className="space-y-2">
          {deleted.map(p => (
            <div key={p.id} className="flex items-center justify-between p-3 bg-white rounded-xl border hover:border-gray-300 transition-all" data-testid={`trash-prospect-${p.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-700 truncate">{p.nom}</p>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  {p.contact && <span>{p.contact}</span>}
                  {p.secteur && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px]">{p.secteur}</span>}
                  {p.deleted_at && <span>Supprime le {new Date(p.deleted_at).toLocaleDateString('fr-FR')}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <Button onClick={() => restore(p.id)} size="sm" className="bg-green-500 hover:bg-green-600 text-white text-xs" data-testid={`restore-${p.id}`}>
                  <RefreshCw size={12} className="mr-1" /> Restaurer
                </Button>
                <Button onClick={() => permDelete(p.id, p.nom)} size="sm" variant="outline" className="text-red-400 border-red-200 hover:bg-red-50 text-xs" data-testid={`perm-delete-${p.id}`}>
                  <X size={12} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
